import {
  Button,
  Center,
  Checkbox,
  Group,
  Loader,
  Modal,
  Paper,
  Stack,
  Text,
} from "@mantine/core"
import { notifications } from "@mantine/notifications"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { getErrorMessage } from "../utils"
import {
  useCopyableIntegrations,
  useCopyIntegrations,
} from "../hooks/useAgencyUsers"

interface Props {
  opened: boolean
  onClose: () => void
  userId: number
}

const COPY_ICONS: Record<number, string> = {
  1: "\u{1F310}",
  2: "\u{1F393}",
  3: "\u{1F30D}",
  4: "\u{1F50D}",
  5: "\u{2699}\u{FE0F}",
  6: "\u{1F4C8}",
  9: "\u{2708}\u{FE0F}",
  10: "\u{1F4CA}",
  11: "\u{1F4E6}",
  12: "\u{1F4F1}",
  13: "\u{1F3E0}",
  14: "\u{1F4B0}",
} satisfies Record<number, string>

export default function CopyIntegrationsModal({
  opened,
  onClose,
  userId,
}: Props) {
  const { t } = useTranslation()
  const { data: copyable, isLoading } = useCopyableIntegrations(userId)
  const copyMutation = useCopyIntegrations()
  const [selected, setSelected] = useState<number[]>([])

  const handleSelectAll = (checked: boolean) => {
    if (checked && copyable) {
      setSelected(copyable.map((c) => c.type))
    } else {
      setSelected([])
    }
  }

  const handleToggle = (type: number) => {
    setSelected((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    )
  }

  const handleCopy = () => {
    if (selected.length === 0) return
    copyMutation.mutate(
      { userId, selection: selected },
      {
        onSuccess: () => {
          notifications.show({
            color: "green",
            title: t("common.success"),
            message: t("agencyUsers.copyIntegrationsSuccess"),
          })
          onClose()
        },
        onError: (err) => {
          notifications.show({
            color: "red",
            title: t("common.error"),
            message: getErrorMessage(err),
          })
        },
      },
    )
  }

  const allSelected =
    copyable && copyable.length > 0 && selected.length === copyable.length

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t("agencyUsers.copyIntegrationsTitle")}
      centered
      size="lg"
    >
      {isLoading ? (
        <Center h={100}>
          <Loader />
        </Center>
      ) : !copyable || copyable.length === 0 ? (
        <Stack align="center" py="lg">
          <Text size="40px" c="gray.3">
            <i className="fa fa-info-circle" />
          </Text>
          <Text c="dimmed" ta="center">
            {t("agencyUsers.noIntegrationsToCopy")}
          </Text>
        </Stack>
      ) : (
        <>
          <Text size="sm" c="dimmed" mb="md">
            {t("agencyUsers.copyIntegrationsHelp")}
          </Text>

          <Paper withBorder p="xs" mb="md" radius="md">
            <Checkbox
              label={t("agencyUsers.selectAll")}
              checked={allSelected}
              indeterminate={selected.length > 0 && !allSelected}
              onChange={(e) => handleSelectAll(e.currentTarget.checked)}
              p="sm"
            />
          </Paper>

          <Stack gap="sm" mb="md">
            {copyable.map((integration) => {
              const icon = COPY_ICONS[integration.type] ?? "\u{1F517}"
              return (
                <Paper
                  key={integration.type}
                  withBorder
                  p="sm"
                  radius="md"
                  style={{
                    borderColor: selected.includes(integration.type)
                      ? "var(--mantine-color-blue-4)"
                      : undefined,
                  }}
                >
                  <Group gap="sm" wrap="nowrap">
                    <Checkbox
                      checked={selected.includes(integration.type)}
                      onChange={() => handleToggle(integration.type)}
                    />
                    <Text size="xl" style={{ lineHeight: 1 }}>
                      {icon}
                    </Text>
                    <Text fw={500} size="sm">
                      {integration.name}
                    </Text>
                  </Group>
                </Paper>
              )
            })}
          </Stack>

          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleCopy}
              loading={copyMutation.isPending}
              disabled={selected.length === 0}
            >
              {t("agencyUsers.copySelected")}
            </Button>
          </Group>
        </>
      )}
    </Modal>
  )
}
